









































import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import PdfTemplateFileDialogComponent from "@/components/admin/clients/PdfTemplateFileDialog.vue";
import { PdfTemplateFieldMapping, PdfTemplateRow } from "@/data/models/ClientAdministrationRow";
import {
  copyClientPdfTemplateForAdministration,
  getClientPdfTemplateFileForAdministration,
  deleteClientPdfTemplateForAdministration,
  getClientPdfTemplatesForAdministration,
  previewClientPdfTemplateForAdministration,
  renameClientPdfTemplateForAdministration,
  replaceClientPdfTemplateFileForAdministration,
} from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  certificateAfterDelete,
  fileActionConsequences,
  fileOperationErrorKey,
  pdfBlobFromBase64,
  PdfTemplateFileAction,
  readFileAsBase64,
} from "@/utils/PdfTemplateFiles";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";
import { saveErrorMessage } from "@/utils/PdfTemplateMapping";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** Seconds a preview's blob link stays valid: long enough for the new tab to load it. */
const PREVIEW_LINK_LIFETIME_MS = 60 * 1000;

/**
 * The file actions of one PDF template page: the download, the preview (the stored mapping, or the page's unsaved one
 * in edit mode), and in view mode replacing the PDF, renaming, copying into the shared templates and
 * deleting. Each names what it reaches before it runs; the names a new file may take are read fresh from
 * IRES Cloud, which also checks them. Emits `changed` (reload), `deleted` (leave the page).
 */
@Component({
  components: {
    "file-dialog": PdfTemplateFileDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class PdfTemplateActionsComponent extends Vue {
  @Prop({ required: true })
  template!: PdfTemplateRow;

  @Prop({ default: null })
  clientId!: string | null;

  @Prop({ default: false })
  isShared!: boolean;

  /** The client's name for the consequence lines; the id stands in when it is unknown. */
  @Prop({ default: "" })
  clientName!: string;

  @Prop({ default: false })
  isCertificateTemplate!: boolean;

  @Prop({ default: false })
  isEditMode!: boolean;

  /** The page's unsaved mapping in edit mode, for the preview; null previews the stored one. */
  @Prop({ default: null })
  previewFields!: PdfTemplateFieldMapping[] | null;

  @Ref("fileDialog")
  fileDialog!: PdfTemplateFileDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get scopeClientId(): string | null {
    return this.isShared ? null : this.clientId;
  }

  get templateName(): string {
    return this.template.fileName || this.template.name || "–";
  }

  consequences(action: PdfTemplateFileAction): string[] {
    return fileActionConsequences(action, {
      isShared: this.isShared,
      clientName: this.clientName,
      isCertificateTemplate: this.isCertificateTemplate,
      templateName: this.templateName,
    }).map((line) => this.$t(line.key, line.params).toString());
  }

  /**
   * The scope's templates, read now: the list may be older than another user's change. Null, with the
   * failure reported, when they cannot be read; the action then does not start.
   */
  async scopeTemplates(clientId: string | null): Promise<PdfTemplateRow[] | null> {
    try {
      return (await getClientPdfTemplatesForAdministration(clientId)).templates;
    } catch {
      SnackbarModule.showError({ key: "admin.pdfTemplates.files.errors.FAILED" });
      return null;
    }
  }

  /** The file names taken in a scope, or null when they cannot be read. */
  async takenFileNames(clientId: string | null): Promise<string[] | null> {
    const templates = await this.scopeTemplates(clientId);
    return templates ? templates.map((template) => template.fileName ?? "") : null;
  }

  /** Runs one change with the busy flag, reporting IRES Cloud's refusal in the user's terms. */
  async run(change: () => Promise<void>) {
    this.busy = true;
    try {
      await change();
    } catch (error) {
      SnackbarModule.showError({ key: fileOperationErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  get pdfIcon(): string {
    return getFileTypeIcon(DownloadFileType.PDF);
  }

  /** A five-minute link from IRES Cloud, logged; an attachment, so the page stays. */
  async onDownload() {
    this.busy = true;
    try {
      window.location.assign(await getClientPdfTemplateFileForAdministration(this.scopeClientId, this.template.id));
    } catch (error) {
      const code = (error as { code?: unknown } | null)?.code;
      SnackbarModule.showError({
        key:
          code === "not-found" || code === "functions/not-found"
            ? "admin.pdfTemplates.fileMissing"
            : "admin.pdfTemplates.downloadFailed",
      });
    } finally {
      this.busy = false;
    }
  }

  async onPreview() {
    // Opened now, while the click still counts as the user's: a tab opened after the call is blocked.
    const tab = window.open("", "_blank");
    if (!tab) {
      SnackbarModule.showError({ key: "admin.pdfTemplates.files.popupBlocked" });
      return;
    }
    tab.opener = null;

    this.busy = true;
    try {
      const preview = await previewClientPdfTemplateForAdministration(this.scopeClientId, this.template.id, this.previewFields);
      const link = URL.createObjectURL(pdfBlobFromBase64(preview.contentBase64));
      tab.location.href = link;
      window.setTimeout(() => URL.revokeObjectURL(link), PREVIEW_LINK_LIFETIME_MS);
    } catch (error) {
      tab.close();
      SnackbarModule.showError(
        this.previewFields ? saveErrorMessage(error) : { key: "admin.pdfTemplates.files.previewFailed" }
      );
    } finally {
      this.busy = false;
    }
  }

  async onReplace() {
    const taken = await this.takenFileNames(this.scopeClientId);
    if (!taken) {
      return;
    }
    const choice = await this.fileDialog.show({
      mode: "replace",
      title: this.$t("admin.pdfTemplates.files.replace").toString(),
      initialName: this.templateName.replace(/\.pdf$/i, ""),
      takenFileNames: taken,
      consequenceLines: this.consequences("replace"),
    });
    if (!choice?.file) {
      return;
    }

    const file = choice.file;
    await this.run(async () => {
      await replaceClientPdfTemplateFileForAdministration(
        this.scopeClientId,
        this.template.id,
        this.template.lastSavedMillis,
        choice.fileName,
        await readFileAsBase64(file)
      );
      SnackbarModule.showSuccess({ key: "admin.pdfTemplates.files.success.replaced" });
      this.$emit("changed");
    });
  }

  async onRename() {
    const taken = await this.takenFileNames(this.scopeClientId);
    if (!taken) {
      return;
    }
    const choice = await this.fileDialog.show({
      mode: "rename",
      title: this.$t("admin.pdfTemplates.files.rename").toString(),
      initialName: this.templateName.replace(/\.pdf$/i, ""),
      takenFileNames: taken,
      consequenceLines: this.consequences("rename"),
    });
    if (!choice) {
      return;
    }

    await this.run(async () => {
      await renameClientPdfTemplateForAdministration(this.scopeClientId, this.template.id, this.template.lastSavedMillis, choice.fileName);
      SnackbarModule.showSuccess({ key: "admin.pdfTemplates.files.success.renamed" });
      this.$emit("changed");
    });
  }

  async onCopyToShared() {
    const taken = await this.takenFileNames(null);
    if (!taken) {
      return;
    }
    const choice = await this.fileDialog.show({
      mode: "copy",
      title: this.$t("admin.pdfTemplates.files.copyToShared").toString(),
      initialName: this.templateName.replace(/\.pdf$/i, ""),
      takenFileNames: taken,
      consequenceLines: this.consequences("copyToShared"),
    });
    if (!choice) {
      return;
    }

    await this.run(async () => {
      await copyClientPdfTemplateForAdministration(this.clientId, this.template.id, null, choice.fileName);
      SnackbarModule.showSuccess({ key: "admin.pdfTemplates.files.success.copied" });
    });
  }

  async onDelete() {
    const templates = await this.scopeTemplates(this.scopeClientId);
    if (!templates) {
      return;
    }
    const certificate = certificateAfterDelete(templates, this.template.id, this.isShared);
    const lines = [
      ...this.consequences("delete"),
      ...(certificate === "UNCHANGED" ? [] : [this.$t(`admin.pdfTemplates.files.consequences.certificate${certificate}`).toString()]),
    ];

    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.pdfTemplates.files.deleteTitle").toString(),
      message: lines.join("\n\n"),
      acceptMessage: this.$t("admin.pdfTemplates.files.delete").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (!confirmed) {
      return;
    }

    await this.run(async () => {
      await deleteClientPdfTemplateForAdministration(this.scopeClientId, this.template.id, this.template.lastSavedMillis);
      SnackbarModule.showSuccess({ key: "admin.pdfTemplates.files.success.deleted" });
      this.$emit("deleted");
    });
  }
}
